import * as React from "react";
import { apiUrl } from "../../utils/api";
import { fetchMyMailboxes } from "./profileApi";
import {
  clampInterval,
  defaultIntervalFor,
  isIntervalCadence,
  type NotificationCadence,
} from "./notificationCadence";

/**
 * Hook für die Benachrichtigungs-Kadenz je zugewiesener Mailbox (SP5).
 *
 * - Lädt `GET /user/notification-preferences` (SP3 liefert IMMER eine Zeile je
 *   zugewiesener Mailbox — das FE muss NICHT mergen) + `GET /mail/mailboxes`
 *   (für die Anzeige-Labels) parallel.
 * - Hält den per-Mailbox-State (`rows`) als einzige Wahrheit (lokal optimistisch).
 * - Mutationen (`setCadence`/`setInterval`/`setEnabled`) patchen optimistisch,
 *   senden ein `PUT` (Single-Item-Batch) und ersetzen `rows` autoritativ mit dem
 *   Voll-Snapshot der Antwort; bei Fehler Rollback auf den Snapshot + Toast.
 * - Fluent-agnostisch: der Toast wird über einen optionalen `notify`-Callback
 *   ausgelöst (M1), der Hook importiert kein Fluent.
 *
 * Bearer-Token wird global durch das gepatchte `fetch` (`utils/api.ts`) gesetzt.
 */

/** FE-Mirror von `NotificationPreferenceItem` (SP3 `notification-preference.dto.ts`). */
export interface NotificationPreferenceItem {
  /** lowercase, normalisiert (= Join-Key zu /mail/mailboxes). */
  mailboxAddress: string;
  cadence: NotificationCadence;
  /** nur bei EVERY_X_HOURS/EVERY_X_MINUTES gesetzt, sonst null. */
  intervalValue: number | null;
  enabled: boolean;
}

interface GetNotificationPreferencesResponse {
  items: NotificationPreferenceItem[];
}

interface UpsertNotificationPreferenceInput {
  mailboxAddress: string;
  cadence: NotificationCadence;
  intervalValue?: number | null;
  enabled?: boolean;
}

/** Angereicherte Zeile (Item + Join-Label + In-Flight-Marker). */
export type NotificationRow = NotificationPreferenceItem & {
  /** angereichert per Join mit /mail/mailboxes; Fallback = mailboxAddress. */
  displayLabel: string;
  /** lokaler In-Flight-Marker für optimistisches Save (signalisiert „busy"). */
  saving: boolean;
};

export type NotifyIntent = "success" | "error";
export type NotifyFn = (message: string, intent: NotifyIntent) => void;

export interface UseNotificationPreferences {
  rows: NotificationRow[];
  loading: boolean;
  loadError: string | null;
  /** optimistisch + PUT (Single-Item-Batch) + Rollback bei Fehler + Toast. */
  setCadence: (mailboxAddress: string, cadence: NotificationCadence) => void;
  setInterval: (mailboxAddress: string, intervalValue: number | null) => void;
  setEnabled: (mailboxAddress: string, enabled: boolean) => void;
  reload: () => void;
}

const PREFS_PATH = "user/notification-preferences";
const INTERVAL_DEBOUNCE_MS = 400;

/** GET /user/notification-preferences (wirft bei !ok). */
async function fetchPreferences(
  signal?: AbortSignal,
): Promise<NotificationPreferenceItem[]> {
  const res = await fetch(apiUrl(PREFS_PATH), { signal });
  if (!res.ok) {
    throw new Error(
      `notification-preferences: ${res.status} ${res.statusText}`,
    );
  }
  const json = (await res.json()) as GetNotificationPreferencesResponse;
  return Array.isArray(json?.items) ? json.items : [];
}

export function useNotificationPreferences(
  notify?: NotifyFn,
): UseNotificationPreferences {
  const [rows, setRows] = React.useState<NotificationRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // Mounted-Guard (kein State-Write nach Unmount) + Label-Map (Join-Quelle).
  const mountedRef = React.useRef(true);
  const labelByAddr = React.useRef<Map<string, string>>(new Map());
  // Pro Adresse ein Debounce-Timer für die Intervall-Zahleingabe.
  const intervalTimers = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  // Aktuelle notify-Funktion ohne den Hook neu zu binden.
  const notifyRef = React.useRef<NotifyFn | undefined>(notify);
  notifyRef.current = notify;

  const emit = React.useCallback((message: string, intent: NotifyIntent) => {
    notifyRef.current?.(message, intent);
  }, []);

  const join = React.useCallback(
    (items: NotificationPreferenceItem[]): NotificationRow[] =>
      items.map((it) => ({
        ...it,
        displayLabel:
          labelByAddr.current.get(it.mailboxAddress) || it.mailboxAddress,
        saving: false,
      })),
    [],
  );

  const load = React.useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setLoadError(null);
      // M3: Prefs sind führend (kritisch), Labels degradierbar (Fallback = Adresse).
      const [prefsResult, mailboxesResult] = await Promise.allSettled([
        fetchPreferences(signal),
        fetchMyMailboxes(signal),
      ]);
      if (signal?.aborted || !mountedRef.current) return;

      if (mailboxesResult.status === "fulfilled") {
        const map = new Map<string, string>();
        for (const mb of mailboxesResult.value) {
          map.set(mb.mailboxAddress, mb.displayName || mb.mailboxAddress);
        }
        labelByAddr.current = map;
      }

      if (prefsResult.status === "rejected") {
        setLoadError(
          "Die Benachrichtigungs-Einstellungen konnten nicht geladen werden.",
        );
        setRows([]);
        setLoading(false);
        return;
      }

      setRows(join(prefsResult.value));
      setLoading(false);
    },
    [join],
  );

  React.useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      mountedRef.current = false;
      controller.abort();
      for (const t of intervalTimers.current.values()) clearTimeout(t);
      intervalTimers.current.clear();
    };
  }, [load]);

  const reload = React.useCallback(() => {
    const controller = new AbortController();
    void load(controller.signal);
  }, [load]);

  /** Patch eine Zeile lokal (immutable). */
  const patchRow = React.useCallback(
    (address: string, patch: Partial<NotificationRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.mailboxAddress === address ? { ...r, ...patch } : r,
        ),
      );
    },
    [],
  );

  /**
   * Sendet das PUT (Single-Item-Batch) und ersetzt `rows` mit dem Voll-Snapshot
   * der Antwort. Bei Fehler Rollback auf den übergebenen Snapshot.
   */
  const commit = React.useCallback(
    async (
      address: string,
      input: UpsertNotificationPreferenceInput,
      snapshot: NotificationRow,
    ) => {
      patchRow(address, { saving: true });
      try {
        const res = await fetch(apiUrl(PREFS_PATH), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [input] }),
        });
        if (!res.ok) {
          throw new Error(`PUT ${PREFS_PATH}: ${res.status} ${res.statusText}`);
        }
        const json = (await res.json()) as GetNotificationPreferencesResponse;
        if (!mountedRef.current) return;
        // Voll-Snapshot autoritativ (korrigiert server-normalisierte Werte).
        setRows(join(Array.isArray(json?.items) ? json.items : []));
      } catch {
        if (!mountedRef.current) return;
        // Rollback auf den Zustand vor der Mutation.
        patchRow(address, { ...snapshot, saving: false });
        emit(
          "Speichern fehlgeschlagen — die Änderung wurde zurückgesetzt.",
          "error",
        );
      }
    },
    [emit, join, patchRow],
  );

  const setCadence = React.useCallback(
    (address: string, cadence: NotificationCadence) => {
      const snapshot = rows.find((r) => r.mailboxAddress === address);
      if (!snapshot) return;

      // Intervall kadenz-abhängig anpassen: keine Intervall-Kadenz → null;
      // Intervall-Kadenz ohne gültigen Wert → sinnvollen Default klammern.
      let intervalValue: number | null;
      if (!isIntervalCadence(cadence)) {
        intervalValue = null;
      } else if (snapshot.intervalValue && snapshot.intervalValue > 0) {
        intervalValue = clampInterval(cadence, snapshot.intervalValue);
      } else {
        intervalValue = clampInterval(cadence, defaultIntervalFor(cadence));
      }

      patchRow(address, { cadence, intervalValue });
      void commit(
        address,
        { mailboxAddress: address, cadence, intervalValue, enabled: snapshot.enabled },
        snapshot,
      );
    },
    [commit, patchRow, rows],
  );

  const setEnabled = React.useCallback(
    (address: string, enabled: boolean) => {
      const snapshot = rows.find((r) => r.mailboxAddress === address);
      if (!snapshot) return;
      patchRow(address, { enabled });
      void commit(
        address,
        {
          mailboxAddress: address,
          cadence: snapshot.cadence,
          intervalValue: snapshot.intervalValue,
          enabled,
        },
        snapshot,
      );
    },
    [commit, patchRow, rows],
  );

  const setInterval = React.useCallback(
    (address: string, intervalValue: number | null) => {
      const snapshot = rows.find((r) => r.mailboxAddress === address);
      if (!snapshot) return;
      // Nur sinnvoll bei Intervall-Kadenz.
      if (!isIntervalCadence(snapshot.cadence)) return;

      // Lokal sofort patchen (auch leeres Feld = null), damit das Tippen flüssig
      // bleibt. PUT erst nach Debounce — und nur bei gültigem Wert (>=1), um
      // 400-Spam bei leerem/ungültigem Feld zu vermeiden (R6).
      patchRow(address, { intervalValue });

      const existing = intervalTimers.current.get(address);
      if (existing) clearTimeout(existing);

      if (intervalValue == null || intervalValue < 1) {
        // Kein PUT, solange das Feld leer/ungültig ist.
        return;
      }
      const clamped = clampInterval(snapshot.cadence, intervalValue);
      const timer = setTimeout(() => {
        intervalTimers.current.delete(address);
        // Snapshot zum Commit-Zeitpunkt frisch lesen (Kadenz kann sich geändert
        // haben); fällt die Kadenz inzwischen weg, kein Intervall-PUT.
        setRows((prev) => {
          const current = prev.find((r) => r.mailboxAddress === address);
          if (!current || !isIntervalCadence(current.cadence)) return prev;
          void commit(
            address,
            {
              mailboxAddress: address,
              cadence: current.cadence,
              intervalValue: clamped,
              enabled: current.enabled,
            },
            current,
          );
          // Geklammerten Wert lokal spiegeln.
          return prev.map((r) =>
            r.mailboxAddress === address ? { ...r, intervalValue: clamped } : r,
          );
        });
      }, INTERVAL_DEBOUNCE_MS);
      intervalTimers.current.set(address, timer);
    },
    [commit, patchRow, rows],
  );

  return { rows, loading, loadError, setCadence, setInterval, setEnabled, reload };
}
